import React, { useMemo, useState } from 'react';
import { X, AlertTriangle, CheckCircle2, Sparkles, Link as LinkIcon } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AssistantResource, {
	type CompiledPlaybookDraft,
	type CompiledPlaybookStep,
	type CompiledPlaybookArgDecision
} from 'Resources/Assistant';
import { notifyAIWorkspaceError } from './notifyAIWorkspaceError';

type Props = {
	draft: CompiledPlaybookDraft;
	/**
	 * When `null`, the draft is saved standalone (no routine binding) — used by
	 * the reply-attached Compile button (WEB-6857). The backend then enforces
	 * source-run ownership instead of routine-edit permission.
	 */
	routineId: number | null;
	/**
	 * Optional context label appended to the title. For routine-bound saves
	 * this is the routine's label; for standalone (reply-initiated) saves it
	 * stays null.
	 */
	routineLabel: string | null;
	/**
	 * Whether the user can persist this draft as a Playbook. The button is
	 * hidden when false (non-editable org routine, or feature flag disabled
	 * upstream). Backend re-checks the permission.
	 */
	canSave: boolean;
	onClose: () => void;
	/** Fired after a successful save so the parent can refresh the routine row. */
	onSaved?: (workflowId: number) => void;
};

const decisionBadge = (decision: CompiledPlaybookArgDecision | undefined): { label: string; icon: JSX.Element } => {
	if (!decision) {
		return {
			label: T('assistant.playbook.decisionUnchanged') || 'unchanged',
			icon: <CheckCircle2 style={{ width: 12, height: 12, opacity: 0.5 }} />
		};
	}
	switch (decision.kind) {
		case 'keep-constant':
			return {
				label: T('assistant.playbook.decisionKeepConstant') || 'constant',
				icon: <CheckCircle2 style={{ width: 12, height: 12 }} />
			};
		case 'keep-binding':
			return {
				label: T('assistant.playbook.decisionKeepBinding') || 'binding',
				icon: <LinkIcon style={{ width: 12, height: 12 }} />
			};
		case 'promote-template':
			return {
				label: T('assistant.playbook.decisionPromoteTemplate') || 'template',
				icon: <LinkIcon style={{ width: 12, height: 12 }} />
			};
		case 'promote-llm-step':
			return {
				label: T('assistant.playbook.decisionPromoteLlmStep') || 'LLM step',
				icon: <Sparkles style={{ width: 12, height: 12 }} />
			};
	}
};

const summariseStep = (step: CompiledPlaybookStep): string => {
	switch (step.kind) {
		case 'tool':
			return `${step.kind} · ${step.tool}`;
		case 'llm':
			return `${step.kind} · → ${step.bind}`;
		case 'if':
			return `${step.kind} · ${step.cond}`;
		case 'foreach':
			return `${step.kind} · ${step.over} as ${step.as}`;
	}
};

const summariseTrigger = (trigger: CompiledPlaybookDraft['workflow']['trigger']): string => {
	const kind = String(trigger.kind);
	if (kind === 'cron' && typeof trigger.cron === 'string') return `cron · ${trigger.cron}`;
	if (kind === 'event') return `event · ${String(trigger.entityType || '?')}.${String(trigger.event || '?')}`;
	return kind;
};

const WorkspacePlaybookDraft = ({ draft, routineId, routineLabel, canSave, onClose, onSaved }: Props) => {
	const [saving, setSaving] = useState(false);
	const handleSave = async () => {
		if (saving) return;
		setSaving(true);
		try {
			const { workflowId } = await AssistantResource.savePlaybookFromCompiled(routineId, draft);
			onSaved?.(workflowId);
		} catch (err) {
			notifyAIWorkspaceError(err as Error, 'savePlaybookFromCompiled');
		} finally {
			setSaving(false);
		}
	};
	const decisionsByStep = useMemo(() => {
		const out: Record<string, Array<{ argPath: string; decision: CompiledPlaybookArgDecision }>> = {};
		for (const [key, decision] of Object.entries(draft.decisions || {})) {
			const sepIdx = key.indexOf('#');
			if (sepIdx < 0) continue;
			const stepId = key.slice(0, sepIdx);
			const argPath = key.slice(sepIdx + 1);
			(out[stepId] = out[stepId] || []).push({ argPath, decision });
		}
		return out;
	}, [draft.decisions]);

	const allWarnings = useMemo(
		() => [
			...(draft.compilerWarnings || []).map(w => ({ ...w, source: 'compiler' as const })),
			...(draft.warnings || []).map(w => ({ ...w, source: 'extractor' as const }))
		],
		[draft.compilerWarnings, draft.warnings]
	);

	return (
		<div className="ws-modal-backdrop" onClick={onClose}>
			<div className="ws-modal ws-playbook-draft" onClick={e => e.stopPropagation()} role="dialog">
				<div className="ws-modal-head">
					<div>
						<h3 style={{ margin: 0 }}>
							{T('assistant.playbook.draftTitle') || 'Playbook draft'}
							{routineLabel ? ` · ${routineLabel}` : ''}
						</h3>
						<div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
							{T('assistant.playbook.compiledFromRun') || 'Compiled from run'} #{draft.sourceRunId}
							{' · '}
							{summariseTrigger(draft.workflow.trigger)}
						</div>
					</div>
					<button
						type="button"
						className="ws-btn ws-btn-ghost"
						onClick={onClose}
						aria-label={T('default.close') || 'Close'}
					>
						<X style={{ width: 14, height: 14 }} />
					</button>
				</div>

				<div className="ws-modal-body" style={{ maxHeight: '60vh', overflow: 'auto' }}>
					{allWarnings.length > 0 && (
						<section style={{ marginBottom: 16 }}>
							<div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
								<AlertTriangle style={{ width: 14, height: 14 }} />
								{T('assistant.playbook.warningsHeading') || 'Warnings'}
								<span style={{ fontWeight: 400, opacity: 0.6, fontSize: 12 }}>({allWarnings.length})</span>
							</div>
							<ul style={{ margin: 0, paddingLeft: 16 }}>
								{allWarnings.map((w, i) => (
									<li key={`${w.source}-${i}`} style={{ fontSize: 12, marginBottom: 4 }}>
										<code style={{ fontSize: 11, opacity: 0.7 }}>
											[{w.source}/{w.code}
											{w.stepId ? `:${w.stepId}` : ''}
											{w.argPath || w.path ? `:${w.argPath || w.path}` : ''}]
										</code>{' '}
										{w.message}
									</li>
								))}
							</ul>
						</section>
					)}

					{draft.validationIssues && draft.validationIssues.length > 0 && (
						<section style={{ marginBottom: 16 }}>
							<div style={{ fontWeight: 600, marginBottom: 6 }}>
								{T('assistant.playbook.validationHeading') || 'Validation issues'}
							</div>
							<ul style={{ margin: 0, paddingLeft: 16 }}>
								{draft.validationIssues.map((v, i) => (
									<li key={i} style={{ fontSize: 12, marginBottom: 4 }}>
										{v.path ? <code style={{ fontSize: 11, opacity: 0.7 }}>[{v.path}]</code> : null} {v.message}
									</li>
								))}
							</ul>
						</section>
					)}

					<section>
						<div style={{ fontWeight: 600, marginBottom: 6 }}>
							{T('assistant.playbook.stepsHeading') || 'Steps'}
							<span style={{ fontWeight: 400, opacity: 0.6, fontSize: 12 }}>
								{' '}
								({draft.workflow.steps.length})
							</span>
						</div>

						{draft.workflow.steps.length === 0 ? (
							<div style={{ fontSize: 12, opacity: 0.6 }}>
								{T('assistant.playbook.noSteps') || 'No steps were extracted from this run.'}
							</div>
						) : (
							<ol style={{ margin: 0, paddingLeft: 16 }}>
								{draft.workflow.steps.map(step => {
									const decisions = decisionsByStep[step.id] || [];
									return (
										<li key={step.id} style={{ marginBottom: 12 }}>
											<div style={{ fontFamily: 'monospace', fontSize: 12 }}>
												<strong>{step.id}</strong> — {summariseStep(step)}
												{'bind' in step && step.bind ? (
													<span style={{ opacity: 0.6 }}> → {step.bind}</span>
												) : null}
											</div>
											{decisions.length > 0 && (
												<ul style={{ margin: '4px 0 0 0', paddingLeft: 16 }}>
													{decisions.map(({ argPath, decision }: { argPath: string; decision: CompiledPlaybookArgDecision }) => {
														const badge = decisionBadge(decision);
														return (
															<li key={argPath} style={{ fontSize: 12, marginBottom: 2 }}>
																<span
																	className="ws-auto-badge"
																	style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 6 }}
																>
																	{badge.icon}
																	{badge.label}
																</span>
																<code style={{ fontSize: 11, opacity: 0.7 }}>{argPath}</code>{' '}
																<span style={{ opacity: 0.7 }}>{decision.rationale}</span>
															</li>
														);
													})}
												</ul>
											)}
										</li>
									);
								})}
							</ol>
						)}
					</section>
				</div>

				<div className="ws-modal-foot" style={{ justifyContent: 'space-between' }}>
					<div style={{ fontSize: 11, opacity: 0.5 }}>
						{T('assistant.playbook.compilerVersion') || 'Compiler'}: {draft.compilerVersion.model}
					</div>
					<div style={{ display: 'flex', gap: 8 }}>
						<button type="button" className="ws-btn" onClick={onClose}>
							{T('default.close') || 'Close'}
						</button>
						{canSave && (
							<button
								type="button"
								className="ws-btn ws-btn-dark"
								onClick={handleSave}
								disabled={saving}
							>
								{saving
									? T('assistant.playbook.saveButtonWorking')
									: T('assistant.playbook.saveButton')}
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default WorkspacePlaybookDraft;
